import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Send, Megaphone, CalendarDays, Armchair, Camera, Trophy, Crown, Gift, ArrowLeft, Heart } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../../components/site/HeroSlides";
import { useGuest } from "./GuestAuthPages";
import { PartyPhotoUpload } from "../../components/party/PartyPhotoUpload";

const card = "bg-white border border-[var(--line)] rounded-2xl p-5";
const H = ({ icon: Icon, children }) => <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Icon className="w-4 h-4 text-[var(--gold)]" />{children}</h3>;

function Top({ me, back }) {
  const nav = useNavigate();
  return (
    <div className="flex items-center justify-between mb-8">
      {back ? <Link to="/guest?all=1" className="text-xs text-[var(--muted)] inline-flex items-center gap-1" data-testid="guest-back"><ArrowLeft className="w-3 h-3" /> My weddings</Link> : <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)]">Guest portal</span>}
      <div className="flex items-center gap-3 text-xs text-[var(--muted)]">
        <span data-testid="guest-me-email">{me.email}</span>
        <button onClick={async () => { await api.post("/guest-auth/logout"); nav("/guest/login"); }} className="inline-flex items-center gap-1 underline" data-testid="guest-logout"><LogOut className="w-3 h-3" /> Log out</button>
      </div>
    </div>
  );
}

export function GuestHome() {
  const me = useGuest();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { if (me) api.get("/guest/portal").then((r) => setRows(r.data)).catch((e) => setErr(formatApiError(e))); }, [me]);
  if (me === false) return <Navigate to="/guest/login" replace />;
  if (err) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)] px-6 text-center" data-testid="guest-home-error">{err}</div>;
  if (!me || !rows) return <div className="min-h-screen bg-[var(--bg)]" />;
  if (rows.length === 1 && !sp.get("all")) return <Navigate to={`/guest/w/${rows[0].wedding_id}`} replace />;
  return (
    <div className="min-h-screen bg-[var(--bg)] px-5 py-10" data-testid="guest-home">
      <div className="max-w-3xl mx-auto">
        <Top me={me} />
        <h1 className="font-serif font-light text-5xl mb-8">Hi {me.name}</h1>
        <div className="grid sm:grid-cols-2 gap-4">
          {rows.map((w) => (
            <Link key={w.wedding_id} to={`/guest/w/${w.wedding_id}`} className={`${card} hover:border-[var(--gold)] transition-colors`} data-testid={`guest-wedding-${w.wedding_id}`}>
              <p className="font-serif text-2xl">{w.couple_names}</p>
              <p className="text-xs text-[var(--muted)]">{w.wedding_date}</p>
              {(w.unread > 0 || w.new_announcements > 0) && <p className="text-xs text-[var(--gold)] mt-2">{w.unread} new message{w.unread === 1 ? "" : "s"} · {w.new_announcements} new update{w.new_announcements === 1 ? "" : "s"}</p>}
            </Link>
          ))}
          {!rows.length && <p className="text-sm text-[var(--muted)]">No weddings linked yet. Open the personal link from your invitation to add one.</p>}
        </div>
      </div>
    </div>
  );
}

function Overview({ d }) {
  const g = d.guest, w = d.wedding;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {d.voice_thanks && (
        <div className="rounded-2xl p-5 md:col-span-2 bg-[#111] text-white" data-testid="guest-voice-thanks">
          <H icon={Heart}>A thank-you from {w.couple_names}</H>
          <audio src={fileUrl(d.voice_thanks.audio_path)} controls className="w-full mb-3" data-testid="guest-voice-thanks-audio" />
          <p className="text-sm text-white/80 whitespace-pre-line">{d.voice_thanks.text}</p>
        </div>
      )}
      <div className={card} data-testid="guest-rsvp-card">
        <H icon={Mail}>My RSVP</H>
        <p className="text-sm">Status: <b className="capitalize" data-testid="guest-rsvp-status">{g.rsvp_status || "pending"}</b></p>
        {g.meal_preference && <p className="text-sm">Meal: {g.meal_preference}</p>}
        <p className="text-sm text-[var(--muted)]">{g.plus_one_allowed ? "You can bring a plus-one" : "Invitation is for you"}</p>
        {w.rsvp_open !== false && <Link to={`/site/${w.slug}/rsvp/${g.guest_id}`} className="btn-outline-gold text-xs mt-3" data-testid="guest-edit-rsvp">Update RSVP</Link>}
      </div>
      <div className={card} data-testid="guest-table-card">
        <H icon={Armchair}>My table</H>
        {d.table && w.seating_public ? <><p className="font-serif text-3xl">{d.table.name}</p><Link to={`/site/${w.slug}/table/${d.table.table_id}`} className="text-xs underline text-[var(--gold)]">Open table page</Link></> : <p className="text-sm text-[var(--muted)]">Your seat will show here once the couple publishes the seating chart.</p>}
        <Link to={`/site/${w.slug}/travel`} className="block text-xs underline text-[var(--muted)] mt-3">Travel & hotel info</Link>
      </div>
      <div className={`${card} md:col-span-2`} data-testid="guest-schedule-card">
        <H icon={CalendarDays}>Schedule</H>
        {d.events.map((e) => (
          <div key={e.event_id} className="flex flex-wrap gap-x-4 py-2 border-b border-[var(--line)]/60 text-sm">
            <span className="w-44 text-[var(--muted)]">{e.event_datetime ? new Date(e.event_datetime).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "TBA"}</span>
            <span className="font-medium">{e.name}</span>{e.venue && <span className="text-[var(--muted)]">{e.venue}</span>}{e.dress_code && <span className="text-[var(--muted)]">Dress: {e.dress_code}</span>}
          </div>
        ))}
        {!d.events.length && <p className="text-sm text-[var(--muted)]">The couple hasn't added the schedule yet.</p>}
      </div>
    </div>
  );
}

function PhotosGames({ d }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className={card} data-testid="guest-photos-card">
        <H icon={Camera}>My photos</H>
        <div className="grid grid-cols-3 gap-2">{d.photos.map((p) => <img key={p.item_id} src={fileUrl(p.path)} alt="" className={`aspect-square object-cover rounded-lg ${p.approved ? "" : "opacity-60"}`} />)}</div>
        {!d.photos.length && <p className="text-sm text-[var(--muted)]">Photos you share show up here.</p>}
        <Link to={`/site/${d.wedding.slug}/upload`} className="btn-outline-gold text-xs mt-3" data-testid="guest-upload-link">Share a photo</Link>
      </div>
      <div className={card} data-testid="guest-games-card">
        <H icon={Trophy}>My game score</H>
        <p className="font-serif text-4xl" data-testid="guest-points">{d.points} <span className="text-base text-[var(--muted)]">points</span></p>
        {d.rank && <p className="text-sm text-[var(--muted)]">Rank #{d.rank} of {d.players}</p>}
        <div className="mt-3 space-y-1">{d.games.map((x) => <Link key={x.code} to={`/party/${x.code}`} className="block text-sm underline text-[var(--gold)]">{x.title}</Link>)}</div>
      </div>
    </div>
  );
}

function PartyGifts({ d, setD }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {d.party && (
        <div className={card} data-testid="guest-party-card">
          <H icon={Crown}>Wedding party to-dos{d.party.role ? ` · ${d.party.role}` : ""}</H>
          <div className="flex items-center gap-3 mb-3">
            <PartyPhotoUpload endpoint={`/guest/portal/${d.wedding.wedding_id}/party-photo`} name={d.guest.name} photo={d.party.photo_url} size="w-14 h-14" testid="guest-party-photo"
              onDone={(url) => setD({ ...d, party: { ...d.party, photo_url: url } })} />
            <p className="text-xs text-[var(--muted)]">Your profile photo shows on the couple's wedding party page. Tap to change it.</p>
          </div>
          {d.party.payments.map((p) => (
            <div key={p.token} className="flex justify-between items-center text-sm py-2 border-b border-[var(--line)]/60">
              <span>{p.title} · ${p.amount}</span>
              {p.status === "paid" ? <span className="text-emerald-700 text-xs">Paid</span> : <Link to={`/pay/attire/${p.token}`} className="text-xs underline text-[var(--gold)]">Pay now</Link>}
            </div>
          ))}
          {d.party.outfits.map((o, i) => <p key={i} className="text-sm py-1">{o.garment_type}{o.fitting_date ? ` · fitting ${o.fitting_date}` : ""}{o.pickup_date ? ` · pickup ${o.pickup_date}` : ""}</p>)}
          {!d.party.payments.length && !d.party.outfits.length && <p className="text-sm text-[var(--muted)]">Nothing to do yet.</p>}
        </div>
      )}
      <div className={card} data-testid="guest-gifts-card">
        <H icon={Gift}>My gifts</H>
        {d.gifts.map((x, i) => (
          <div key={i} className="py-2 border-b border-[var(--line)]/60 text-sm">
            <p>{x.gift}{x.amount ? ` · $${x.amount}` : ""}</p>
            {x.thank_you ? <p className="text-[var(--muted)] italic mt-1 whitespace-pre-line" data-testid={`guest-thanks-${i}`}>"{x.thank_you}"</p> : <p className="text-xs text-[var(--muted)]">Thank-you note coming soon</p>}
          </div>
        ))}
        {!d.gifts.length && <p className="text-sm text-[var(--muted)]">Gifts you send show up here, with the couple's thank-you note.</p>}
      </div>
    </div>
  );
}

function Chat({ wid, names }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/guest/portal/${wid}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [wid]); // eslint-disable-line
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  async function send(e) {
    e.preventDefault();
    try { await api.post(`/guest/portal/${wid}/messages`, { text }); setText(""); load(); } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className={card} data-testid="guest-chat">
      <H icon={Send}>Message {names}</H>
      <p className="text-xs text-[var(--muted)] mb-3">Private. Only you, the couple and their planner can see this.</p>
      <div className="h-80 overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.map((m) => (
          <div key={m.msg_id} className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender === "guest" ? "ml-auto bg-[#111] text-white" : "bg-[var(--bg-warm)]"}`} data-testid={`guest-msg-${m.msg_id}`}>
            {m.sender !== "guest" && <p className="text-[10px] text-[var(--gold)]">{m.author}</p>}<p className="whitespace-pre-line">{m.text}</p>
          </div>
        ))}
        {!msgs.length && <p className="text-sm text-[var(--muted)]">Ask anything: plus-ones, dietary needs, travel…</p>}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input required value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Write a message…" className="input-luxe flex-1" data-testid="guest-chat-input" />
        <button className="btn-gold text-xs" data-testid="guest-chat-send"><Send className="w-3 h-3" /> Send</button>
      </form>
    </div>
  );
}

function Announcements({ wid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/guest/portal/${wid}/announcements`).then((r) => setRows(r.data)).catch(() => {}); }, [wid]);
  return (
    <div className={card} data-testid="guest-announcements">
      <H icon={Megaphone}>Updates from the couple</H>
      {rows.map((a) => (
        <div key={a.ann_id} className="py-3 border-b border-[var(--line)]/60" data-testid={`guest-ann-${a.ann_id}`}>
          <p className="font-medium">{a.title}</p><p className="text-sm whitespace-pre-line">{a.text}</p>
          <p className="text-[10px] text-[var(--muted)] mt-1">{new Date(a.at).toLocaleString()}</p>
        </div>
      ))}
      {!rows.length && <p className="text-sm text-[var(--muted)]">No updates yet.</p>}
    </div>
  );
}

const TABS = [["overview", "My day"], ["messages", "Messages"], ["updates", "Updates"], ["photos", "Photos & games"], ["more", "Gifts & to-dos"]];

export function GuestWedding() {
  const { weddingId } = useParams();
  const me = useGuest();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => { if (me) api.get(`/guest/portal/${weddingId}`).then((r) => setD(r.data)).catch((e) => setD({ error: formatApiError(e) })); }, [me, weddingId]);
  if (me === false) return <Navigate to="/guest/login" replace />;
  if (!me || !d) return <div className="min-h-screen bg-[var(--bg)]" />;
  if (d.error) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]" data-testid="guest-wedding-error">{d.error}</div>;
  return (
    <div className="min-h-screen bg-[var(--bg)] px-5 py-10" data-testid="guest-wedding">
      <div className="max-w-4xl mx-auto">
        <Top me={me} back />
        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)]">Hi {d.guest.name}</p>
        <h1 className="font-serif font-light text-5xl mb-6">{d.wedding.couple_names}</h1>
        <div className="flex gap-2 overflow-x-auto mb-6 pb-1">
          {TABS.map(([id, l]) => {
            const n = id === "messages" ? d.unread : id === "updates" ? d.new_announcements : 0;
            return (
              <button key={id} onClick={() => { setTab(id); if (n) setD({ ...d, [id === "messages" ? "unread" : "new_announcements"]: 0 }); }} data-testid={`guest-tab-${id}`} className={`shrink-0 px-4 py-2 rounded-full text-xs transition-colors inline-flex items-center gap-1.5 ${tab === id ? "bg-[#111] text-white" : "bg-white border border-[var(--line)]"}`}>
                {l}{n > 0 && <span className="min-w-[18px] h-[18px] rounded-full bg-[var(--gold)] text-white text-[10px] flex items-center justify-center px-1" data-testid={`guest-tab-badge-${id}`}>{n}</span>}
              </button>
            );
          })}
        </div>
        {tab === "overview" && <Overview d={d} />}
        {tab === "messages" && <Chat wid={weddingId} names={d.wedding.couple_names} />}
        {tab === "updates" && <Announcements wid={weddingId} />}
        {tab === "photos" && <PhotosGames d={d} />}
        {tab === "more" && <PartyGifts d={d} setD={setD} />}
      </div>
    </div>
  );
}
